import { Box, Stack, Typography } from '@mui/material';
import {
  ComponentType,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider } from '@/components/react-hook-form';
import { useForm } from '@/components/react-hook-form/hooks';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { HouseholdChatNavigationState } from '@/modules/household/chat/householdChatDeepLink';
import {
  useCreateHouseholdV2Mutation,
  useUpdateHouseholdMutation,
} from '@/modules/household/graphql/Households.generated';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';

import { SmartReviewOnboardingHeaderAction } from './components/SmartReviewOnboardingHeaderAction';
import { SmartReviewOnboardingStepTracker } from './components/SmartReviewOnboardingStepTracker';
import { initialHouseholdSetupStorageKey } from './initialHouseholdSetup';
import {
  getSmartReviewStepOrder,
  SmartReviewOnboardingSection,
  SmartReviewStep,
  SmartReviewStepFooterProps,
} from './SmartReviewOnboardingModal.types';
import {
  clearDismissedSmartReviewStep,
  getDismissedSmartReviewStep,
  setDismissedSmartReviewStep,
} from './smartReviewOnboardingStep';
import { ClientSetup } from './steps/clientSetup';
import {
  ClientSetupFormShape,
  defaultClientSetupClientFormShape,
} from './steps/clientSetup/ClientSetup.type';
import { mapClientSetupFormToCreateHouseholdInput } from './steps/clientSetup/mapClientSetupFormToCreateHouseholdInput';
import { DocumentUpload } from './steps/documentUpload';
import { DocumentUploadStepProvider } from './steps/documentUpload/context/DocumentUploadStep.provider';
import {
  ONBOARDING_WATERFALL_GENERATION_QUERY_PARAM,
  PENDING_ONBOARDING_WATERFALL_TRACKER_ID,
} from './steps/noDocumentsPlan/noDocumentsPlan.types';
import { mapNoDocumentsPromptToChatTemplate } from './steps/noDocumentsPlan/noDocumentsPlanPrompts';
import {
  NoDocumentsPlanBuildInput,
  NoDocumentsPlanSection,
} from './steps/noDocumentsPlan/NoDocumentsPlanTrowser';
import { PresentationTemplateSelectionStep } from './steps/presentation';
import { useCompleteHouseholdOnboardingMutation } from './steps/presentation/graphql/OnboardingPresentation.generated';
import { PresentationStepContextValue } from './steps/presentation/PresentationTemplateSelectionStep.context';
import { PresentationStepProvider } from './steps/presentation/PresentationTemplateSelectionStep.provider';
import { SmartReviewOption } from './steps/smartReviewOption';
import {
  SmartReviewStepContext,
  SmartReviewStepContextValue,
} from './steps/smartReviewOption/context/smartReviewStep.context';

declare module '@/modules/navigation/trowsers/trowser.types' {
  interface TrowserDataMap {
    [TrowserKey.SmartReviewOnboarding]: {
      section?: SmartReviewOnboardingSection;
    };
  }
}

export interface SmartReviewOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialHouseholdId?: string;
  initialHouseholdClients?: Array<{
    firstName: string;
    lastName: string;
    stateCode: string;
  }>;
  initialStep?: SmartReviewStep;
}

interface StepComponents {
  Body: ComponentType;
  Footer: ComponentType<SmartReviewStepFooterProps>;
}

interface OnboardingStepContextProvidersProps {
  presentation: PresentationStepContextValue;
  smartReview: SmartReviewStepContextValue;
}

function OnboardingStepContextProviders({
  children,
  presentation,
  smartReview,
}: PropsWithChildren<OnboardingStepContextProvidersProps>) {
  return (
    <PresentationStepProvider value={presentation}>
      <SmartReviewStepContext.Provider value={smartReview}>
        {children}
      </SmartReviewStepContext.Provider>
    </PresentationStepProvider>
  );
}

/** Body + Footer for each step, rendered based on the current step. */
const STEP_COMPONENTS: Record<SmartReviewStep, StepComponents> = {
  [SmartReviewStep.ClientSetup]: {
    Body: ClientSetup.Body,
    Footer: ClientSetup.Footer,
  },
  [SmartReviewStep.DocumentUpload]: {
    Body: DocumentUpload.Body,
    Footer: DocumentUpload.Footer,
  },
  [SmartReviewStep.SmartReviewOption]: {
    Body: SmartReviewOption.Body,
    Footer: SmartReviewOption.Footer,
  },
  [SmartReviewStep.Presentation]: {
    Body: PresentationTemplateSelectionStep.Body,
    Footer: PresentationTemplateSelectionStep.Footer,
  },
};

/**
 * SmartReview client onboarding flow.
 * A single-pane multi-step wizard whose title and step tracker are constant while
 * the body and footer are driven by the current step.
 */
export function SmartReviewOnboardingModal({
  isOpen,
  onClose,
  initialHouseholdId,
  initialHouseholdClients,
  initialStep = SmartReviewStep.ClientSetup,
}: SmartReviewOnboardingModalProps) {
  const currentUser = useCurrentUser();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const navigateReactRouter = useNavigate();
  const onboardingTrowser = useTrowser(TrowserKey.SmartReviewOnboarding);
  const { isSmartReviewEnabled } = useTenantDetailsContext();
  const trackUserEvent = useTrackUserEvent();
  const [currentStep, setCurrentStep] = useState<SmartReviewStep>(initialStep);
  const [isHouseholdSmartReviewEnabled, setIsHouseholdSmartReviewEnabled] =
    useState(isSmartReviewEnabled);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  // The household created by the "Basic info" step. Held so later steps
  // (e.g. document upload) can attach to it, and so re-submitting the first
  // step doesn't create a duplicate household.
  const [householdId, setHouseholdId] = useState<string | null>(
    initialHouseholdId ?? null
  );
  const [initialSetupTrackerId, setInitialSetupTrackerId] =
    useLocalStorage<string>(initialHouseholdSetupStorageKey(householdId ?? ''));
  useEffect(() => {
    // Only a new household created in this wizard gets initial-run provenance.
    // Resumed households keep their existing marker; later uploads cannot claim it.
    if (householdId && !initialHouseholdId && !initialSetupTrackerId) {
      setInitialSetupTrackerId('created');
    }
  }, [
    householdId,
    initialHouseholdId,
    initialSetupTrackerId,
    setInitialSetupTrackerId,
  ]);
  const stepOrder = useMemo(
    () => getSmartReviewStepOrder(isSmartReviewEnabled),
    [isSmartReviewEnabled]
  );
  const activeStep =
    !isSmartReviewEnabled && currentStep === SmartReviewStep.SmartReviewOption
      ? SmartReviewStep.Presentation
      : currentStep;
  const activeIndex = stepOrder.indexOf(activeStep);

  useEffect(() => {
    if (activeStep !== currentStep) setCurrentStep(activeStep);
    if (!isSmartReviewEnabled) setIsHouseholdSmartReviewEnabled(false);
  }, [activeStep, currentStep, isSmartReviewEnabled]);

  useEffect(() => {
    if (!isOpen || activeStep !== SmartReviewStep.SmartReviewOption) return;
    trackUserEvent('client_onboarding smartreview option viewed', {
      householdId: householdId ?? initialHouseholdId,
    });
  }, [activeStep, householdId, initialHouseholdId, isOpen, trackUserEvent]);

  // The furthest step reached in this session. The step tracker lets the user
  // go back to any reached step, and forward again up to this step. Derived
  // during render (the "adjust state during render" pattern) so it is always
  // current for every path that changes the step.
  const [furthestStepIndex, setFurthestStepIndex] = useState(activeIndex);
  if (activeIndex > furthestStepIndex) setFurthestStepIndex(activeIndex);

  // A key that increments each time the modal opens, used to remount the
  // document-upload provider so every session starts from a fresh instance.
  // This discards the previous session's uploaded files and persistence flag,
  // and neutralizes any late-arriving async callbacks from a prior session
  // (their setState targets an unmounted instance). Derived during render (the
  // "adjust state during render" pattern) so the key is set before paint; it's
  // stable within a session, so moving between steps never remounts the shell.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [openSessionId, setOpenSessionId] = useState(0);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setOpenSessionId((id) => id + 1);
  }

  // The form is owned here (not per-step) so its values persist as the user
  // moves between steps. Steps consume it via useFormContext.
  const formMethods = useForm<ClientSetupFormShape>({
    defaultValues: {
      clientOne: {
        ...defaultClientSetupClientFormShape,
        ...initialHouseholdClients?.[0],
      },
      clientTwo: {
        ...defaultClientSetupClientFormShape,
        ...initialHouseholdClients?.[1],
      },
      primaryRelationshipOwnerId: currentUser?.id ?? '',
      subBrandId: '',
      teamIds: [],
      integrationClientIds: [],
      isProspect: false,
      relationshipToBetweenPrimaryClients: '',
      defaultDeathOrder: 'primary',
    },
  });
  const { setValue, getValues, handleSubmit } = formMethods;

  useEffect(() => {
    if (!initialHouseholdClients?.length) return;

    const { clientOne, clientTwo } = getValues();
    const formAlreadyHasClients = Boolean(
      clientOne.firstName ||
        clientOne.lastName ||
        clientTwo.firstName ||
        clientTwo.lastName
    );
    if (formAlreadyHasClients) return;

    // The household query can resolve after the resumed modal mounts. Keep any
    // in-progress form values, but hydrate an empty resumed form when it does.
    setValue('clientOne', {
      ...defaultClientSetupClientFormShape,
      ...initialHouseholdClients[0],
    });
    setValue('clientTwo', {
      ...defaultClientSetupClientFormShape,
      ...initialHouseholdClients[1],
    });
  }, [getValues, initialHouseholdClients, setValue]);

  const [createHouseholdV2] = useCreateHouseholdV2Mutation({
    onError: (error) => {
      showFeedback(
        'Encountered an error creating a new client. Please try again.'
      );
      reportError('Caught error creating household in SmartReview flow', error);
    },
    // Refetch the client list (and the "has created households" gate) so the
    // new household appears when the user returns to the list.
    refetchQueries: ['ListClients', 'UserHasCreatedHouseholdsQuery'],
  });

  const [updateHousehold] = useUpdateHouseholdMutation({
    onError: (error) => {
      showFeedback('Could not update the SmartReview setting.');
      reportError('Error setting SmartReview enablement', error);
    },
  });
  const [completeHouseholdOnboarding, { loading: isCompletingOnboarding }] =
    useCompleteHouseholdOnboardingMutation({
      onError: (error) => {
        showFeedback('Could not complete client onboarding.');
        reportError('Error completing SmartReview onboarding', error);
      },
    });

  // The current user can resolve after mount. Fill missing defaults without
  // replacing a choice the user has already made.
  useEffect(() => {
    if (!isOpen || !currentUser) return;
    if (!getValues('primaryRelationshipOwnerId')) {
      setValue('primaryRelationshipOwnerId', currentUser.id);
    }
    const teamIds = currentUser.teamIds ?? [];
    if (teamIds.length === 1 && getValues('teamIds').length === 0) {
      setValue('teamIds', teamIds);
    }
  }, [isOpen, currentUser, setValue, getValues]);

  // All hosts unmount the wizard when it closes. Keep the current step until
  // then so a pending route transition does not flash the Basic info screen.
  const prepareForHouseholdNavigation = useCallback(() => {
    if (householdId) {
      setDismissedSmartReviewStep(householdId, activeStep);
    }
  }, [activeStep, householdId]);

  const handleClose = useCallback(() => {
    prepareForHouseholdNavigation();
    if (!householdId) {
      onClose();
      return;
    }
    const path = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
      { householdId }
    );
    navigateReactRouter(path, { replace: true });
  }, [
    householdId,
    navigateReactRouter,
    onClose,
    prepareForHouseholdNavigation,
  ]);

  const handleBuildPlan = useCallback(
    ({ advisorContext, templateId }: NoDocumentsPlanBuildInput) => {
      if (!householdId) {
        reportError(
          'Missing household after the SmartReview Basic info step',
          new Error('Cannot build a no-documents plan without a household')
        );
        const householdListPath = getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLDS_LIST,
          {}
        );
        navigateReactRouter(householdListPath, { replace: true });
        return;
      }

      const path = getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB,
        { householdId },
        {
          [ONBOARDING_WATERFALL_GENERATION_QUERY_PARAM]:
            PENDING_ONBOARDING_WATERFALL_TRACKER_ID,
        }
      );
      // After navigation, useSendInitialHouseholdChatMessage consumes this state
      // and sends message and withPromptTemplate as SendHouseholdChatMessage input.
      const navigationState: HouseholdChatNavigationState = {
        initialHouseholdChatMessage: {
          message: advisorContext,
          withPromptTemplate: mapNoDocumentsPromptToChatTemplate(templateId),
          workflowTrackerQueryParam:
            ONBOARDING_WATERFALL_GENERATION_QUERY_PARAM,
        },
      };

      clearDismissedSmartReviewStep(householdId);

      // Navigating away unmounts the wizard and clears its local state. Do not
      // reset first, because that paints the Basic info step before the route
      // transition completes.
      navigateReactRouter(path, { replace: true, state: navigationState });
    },
    [householdId, navigateReactRouter, reportError]
  );

  const handleAdvance = useCallback(() => {
    const nextStep = stepOrder[activeIndex + 1];
    if (nextStep) setCurrentStep(nextStep);
  }, [activeIndex, stepOrder]);

  const handleBack = useCallback(() => {
    const prevStep = stepOrder[activeIndex - 1];
    if (prevStep) setCurrentStep(prevStep);
  }, [activeIndex, stepOrder]);

  // "Continue" on the Basic info step submits the form: it creates the
  // household, then advances to the Documents step (staying in the wizard rather
  // than navigating to the new household). Validation is handled by
  // handleSubmit. If the household was already created, we skip creation to
  // avoid duplicates and just advance.
  const submitClientSetup = handleSubmit(async (values) => {
    if (householdId) {
      handleAdvance();
      return;
    }

    try {
      const result = await createHouseholdV2({
        variables: {
          input: mapClientSetupFormToCreateHouseholdInput(values),
          relationshipToBetweenPrimaryClients:
            values.relationshipToBetweenPrimaryClients || undefined,
        },
      });
      const createdId = result.data?.createHouseholdV2.id;
      if (createdId) {
        setHouseholdId(createdId);
        handleAdvance();
      }
    } catch {
      // Surfaced to the user via the mutation's onError handler; stay on this
      // step so they can retry.
    }
  });

  // Returns true when the SmartReview setting saved. The mutation's onError
  // handler tells the user about a failure.
  const saveSmartReviewSetting = useCallback(async (): Promise<boolean> => {
    if (!householdId || !currentUser) return false;

    let saved = false;
    try {
      const result = await updateHousehold({
        variables: {
          id: householdId,
          input: { enableSmartReview: isHouseholdSmartReviewEnabled },
        },
      });
      // Apollo can resolve with errors when onError handles the failure.
      saved = Boolean(result.data?.updateHousehold) && !result.errors?.length;
    } catch {
      // The mutation's onError handler reports the failure to the user.
    }
    trackUserEvent(
      saved
        ? 'client_onboarding smartreview setting saved'
        : 'client_onboarding smartreview setting save failed',
      {
        householdId,
        enabled: isHouseholdSmartReviewEnabled,
      }
    );
    return saved;
  }, [
    householdId,
    currentUser,
    isHouseholdSmartReviewEnabled,
    trackUserEvent,
    updateHousehold,
  ]);

  // SmartReview is now an intermediate step. Its primary action only persists
  // the setting and advances; the Presentation step owns final completion.
  const completeSmartReviewStep = useCallback(async () => {
    if (await saveSmartReviewSetting()) {
      setCurrentStep(SmartReviewStep.Presentation);
    }
  }, [saveSmartReviewSetting]);

  // Step-tracker navigation. Going forward from SmartReview saves its setting
  // first, the same as its Continue. Going back discards unsaved changes, the
  // same as its Previous button.
  const handleStepTrackerNavigate = useCallback(
    async (step: SmartReviewStep) => {
      const isForward = stepOrder.indexOf(step) > activeIndex;
      if (
        activeStep === SmartReviewStep.SmartReviewOption &&
        isForward &&
        !(await saveSmartReviewSetting())
      ) {
        return;
      }
      setCurrentStep(step);
    },
    [activeIndex, activeStep, saveSmartReviewSetting, stepOrder]
  );

  // The backend returns once the completion workflow starts. Navigate at that
  // point so the existing overview banner can show its progress tracker.
  const finishOnboarding = useCallback(
    async (presentationTemplateIds: string[]) => {
      if (!householdId) return;

      try {
        const result = await completeHouseholdOnboarding({
          variables: {
            householdID: householdId,
            presentationTemplateIDs: presentationTemplateIds,
          },
        });
        const trackerId = result.data?.completeHouseholdOnboarding.id;
        if (trackerId && initialSetupTrackerId === 'created') {
          setInitialSetupTrackerId(trackerId);
        }
      } catch {
        return;
      }

      if (getDismissedSmartReviewStep(householdId)) {
        clearDismissedSmartReviewStep(householdId);
      }
      const path = getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
        { householdId }
      );
      navigateReactRouter(path, { replace: true });
    },
    [
      householdId,
      completeHouseholdOnboarding,
      navigateReactRouter,
      initialSetupTrackerId,
      setInitialSetupTrackerId,
    ]
  );

  // Each step's primary action: Basic info submits (creates the household),
  // SmartReview persists its setting, and the other intermediate steps advance.
  const resolveOnAdvance = () => {
    if (activeStep === SmartReviewStep.ClientSetup) return submitClientSetup;
    if (activeStep === SmartReviewStep.SmartReviewOption) {
      return completeSmartReviewStep;
    }
    return handleAdvance;
  };

  const smartReviewStepValue = useMemo<SmartReviewStepContextValue>(
    () => ({
      isSmartReviewEnabled: isHouseholdSmartReviewEnabled,
      setIsSmartReviewEnabled: setIsHouseholdSmartReviewEnabled,
    }),
    [isHouseholdSmartReviewEnabled]
  );
  const presentationStepValue = useMemo(
    () => ({
      selectedTemplateIds,
      setSelectedTemplateIds,
      completeOnboarding: finishOnboarding,
      isCompleting: isCompletingOnboarding,
    }),
    [selectedTemplateIds, finishOnboarding, isCompletingOnboarding]
  );

  const { Body, Footer } = STEP_COMPONENTS[activeStep];
  const isNoDocumentsPlanOpen =
    activeStep === SmartReviewStep.DocumentUpload &&
    onboardingTrowser.isOpen &&
    onboardingTrowser.target === householdId &&
    onboardingTrowser.section === SmartReviewOnboardingSection.NoDocumentsPlan;

  const footerProps: SmartReviewStepFooterProps = {
    onAdvance: resolveOnAdvance(),
    onBack: handleBack,
    onClose: handleClose,
    isFirstStep: activeIndex === 0,
    isLastStep: activeIndex === stepOrder.length - 1,
  };

  const handleBackToDocuments = useCallback(() => {
    onboardingTrowser.open({ target: householdId ?? undefined });
  }, [householdId, onboardingTrowser]);

  return (
    <FormProvider {...formMethods}>
      {/* The document-upload step's Body and Footer render into separate DOM
          regions of the Modal; this provider wraps the whole Modal (mounted
          unconditionally, so the shell is never remounted between steps) so
          both can share the uploader + overlay state. */}
      <DocumentUploadStepProvider
        key={openSessionId}
        isOpen={isOpen}
        householdId={householdId}
        onCloseWizard={prepareForHouseholdNavigation}
      >
        <OnboardingStepContextProviders
          presentation={presentationStepValue}
          smartReview={smartReviewStepValue}
        >
          <FullScreenModal
            trowserKey={TrowserKey.SmartReviewOnboarding}
            isOpen={isOpen}
            onClose={handleClose}
          >
            {isNoDocumentsPlanOpen ? (
              <NoDocumentsPlanSection
                onBack={handleBackToDocuments}
                onBuildPlan={handleBuildPlan}
              />
            ) : (
              <Stack height="100%" bgcolor={COLORS.PRIMITIVES.WHITE}>
                <Stack
                  component="header"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    height: 60,
                    minHeight: 60,
                    px: 2,
                    borderBottom: `1px solid ${COLORS.GRAY[200]}`,
                  }}
                >
                  <Typography variant="title-l">Client onboarding</Typography>
                  <SmartReviewOnboardingHeaderAction
                    isFirstStep={activeIndex === 0}
                    onClose={handleClose}
                  />
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: COLORS.BLUE[50],
                    borderBottom: `solid 1px ${COLORS.GRAY[200]}`,
                    p: 3,
                    flexShrink: 0,
                  }}
                >
                  <SmartReviewOnboardingStepTracker
                    currentStep={activeStep}
                    stepOrder={stepOrder}
                    furthestStepIndex={furthestStepIndex}
                    disabled={isCompletingOnboarding}
                    onNavigate={handleStepTrackerNavigate}
                  />
                </Box>
                <Box
                  sx={{
                    px: 8,
                    pt: activeStep === SmartReviewStep.Presentation ? 4 : 8,
                    pb: activeStep === SmartReviewStep.Presentation ? 2 : 8,
                    flex: 1,
                    minHeight: 0,
                    position: 'relative',
                    overflowY: 'auto',
                  }}
                >
                  <Body />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    height: 64,
                    minHeight: 64,
                    px: 2,
                    borderTop: `1px solid ${COLORS.GRAY[200]}`,
                  }}
                >
                  <Footer {...footerProps} />
                </Stack>
              </Stack>
            )}
          </FullScreenModal>
        </OnboardingStepContextProviders>
      </DocumentUploadStepProvider>
    </FormProvider>
  );
}
